'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  Button,
  Combobox,
  createListCollection,
  LegacyDialog,
  IconButton,
  Input,
  LabelDefault,
  SegmentGroupHorizontal,
} from '@terra/ui-design-system';
import { ArrowUpDownIcon, Cancel01Icon, Tick01Icon } from '@terra/ui-icons';
import { HStack, VStack } from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import { isReassociateHarEntriesConflictError } from '../../clients/tester-client/portal/reassociate-har-entries.schemas';
import { useEndpointsPreview } from '../../hooks/api/useEndpointsPreview';
import { useReassociateHarEntries } from '../../hooks/api/useReassociateHarEntries';
import type { HARRequest } from '../../hooks/api/mapHarEntryToHarRequest';
import { ReassociateCreateFromHarsFields } from './ReassociateCreateFromHarsFields';
import {
  findCreateCollision,
  toCreateFromHarsTarget,
  toPlannedHarCreate,
} from '../../utils/reassociate-har-entries/har-reassociate-create-collisions';
import {
  canCreateFromHars,
  defaultPathsByCreateGroup,
  existingTargetIdFromActiveCollision,
  groupHarRowsForCreate,
} from '../../utils/reassociate-har-entries/har-reassociate-identity';
import {
  filterEndpointUriOptions,
  toEndpointUriOption,
  withSelectedEndpointOption,
} from '../../utils/reassociate-har-entries/endpoint-uri-label';
import { willEmptySourceEndpoint } from '../../utils/reassociate-har-entries/will-empty-source-endpoint';
import { REQUESTS_REASSOCIATE_AUTOMATION_IDS } from '../tabs/requestsReassociateAutomationIds';

type ReassociateMode = 'existing' | 'createFromHars';

type ReassociateHarEntriesDialogProps = {
  isOpen: boolean;
  applicationId: string;
  sourceEndpointId: string;
  selectedRows: readonly HARRequest[];
  sourceHarTotalCount: number;
  onOpenChange: (open: boolean) => void;
  onMoved?: () => void;
};

export function ReassociateHarEntriesDialog({
  isOpen,
  applicationId,
  sourceEndpointId,
  selectedRows,
  sourceHarTotalCount,
  onOpenChange,
  onMoved,
}: ReassociateHarEntriesDialogProps) {
  const t = useTranslations('proxy.requests.reassociate');
  const tCommon = useTranslations('common');
  const { endpoints, isLoading } = useEndpointsPreview({ applicationId });
  const { mutateAsync, isPending } = useReassociateHarEntries();
  const [mode, setMode] = useState<ReassociateMode>('existing');
  const [selectedEndpointId, setSelectedEndpointId] = useState('');
  const [pathsByGroupId, setPathsByGroupId] = useState<Record<string, string>>(
    {},
  );
  const [endpointQuery, setEndpointQuery] = useState('');
  const createGroups = useMemo(
    () => groupHarRowsForCreate(selectedRows),
    [selectedRows],
  );
  const emptiesSource = willEmptySourceEndpoint({
    selectedCount: selectedRows.length,
    sourceHarTotalCount,
  });
  const canCreate = createGroups.every((group) =>
    canCreateFromHars({
      path: pathsByGroupId[group.id] ?? '',
      rows: group.rows,
    }),
  );
  const createCollision = useMemo(
    () =>
      findCreateCollision({
        planned: createGroups.flatMap((group) => {
          const first = group.rows[0];

          if (!first) {
            return [];
          }

          return [
            toPlannedHarCreate({
              row: first,
              path: pathsByGroupId[group.id] ?? '',
            }),
          ];
        }),
        existing: endpoints.map((endpoint) => ({
          id: endpoint.id,
          method: endpoint.request.method,
          host: endpoint.host,
          path: endpoint.path,
          graphqlOperationName: endpoint.graphqlOperationName,
          soapOperationName: endpoint.soapOperationName,
          xmlOperationName: endpoint.xmlOperationName,
          mcpOperationName: endpoint.mcpOperationName,
          grpcServiceName: endpoint.grpcServiceName,
          grpcMethodName: endpoint.grpcMethodName,
        })),
        sourceEndpointId,
      }),
    [createGroups, endpoints, pathsByGroupId, sourceEndpointId],
  );
  const endpointItems = useMemo(
    () =>
      endpoints
        .filter((endpoint) => endpoint.id !== sourceEndpointId)
        .map((endpoint) =>
          toEndpointUriOption({
            id: endpoint.id,
            path: endpoint.path,
            method: endpoint.request.method,
            host: endpoint.host,
          }),
        ),
    [endpoints, sourceEndpointId],
  );
  const absorbableEndpointItems = useMemo(
    () =>
      filterEndpointUriOptions({
        items: endpointItems,
        query: '',
        rows: selectedRows,
      }),
    [endpointItems, selectedRows],
  );
  const visibleEndpointItems = useMemo(
    () =>
      filterEndpointUriOptions({
        items: absorbableEndpointItems,
        query: endpointQuery,
      }),
    [absorbableEndpointItems, endpointQuery],
  );
  const hasAbsorbableEndpoints = absorbableEndpointItems.length > 0;
  const showNoValidEndpoints = !isLoading && !hasAbsorbableEndpoints;
  const collection = useMemo(
    () =>
      createListCollection({
        items: withSelectedEndpointOption({
          items: visibleEndpointItems,
          selectedId: selectedEndpointId,
          catalog: absorbableEndpointItems,
        }),
      }),
    [absorbableEndpointItems, selectedEndpointId, visibleEndpointItems],
  );
  const canConfirm =
    selectedRows.length > 0 &&
    !emptiesSource &&
    (mode === 'existing'
      ? selectedEndpointId.length > 0
      : canCreate && !createCollision);
  const selectedRowsRef = useRef(selectedRows);

  selectedRowsRef.current = selectedRows;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setMode('existing');
    setSelectedEndpointId('');
    setEndpointQuery('');
    setPathsByGroupId(
      defaultPathsByCreateGroup(groupHarRowsForCreate(selectedRowsRef.current)),
    );
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setPathsByGroupId((current) => {
      const defaults = defaultPathsByCreateGroup(createGroups);
      const next = { ...current };

      for (const [groupId, path] of Object.entries(defaults)) {
        if (!next[groupId]) {
          next[groupId] = path;
        }
      }

      return next;
    });
  }, [createGroups, isOpen]);

  useEffect(() => {
    if (!selectedEndpointId) {
      return;
    }

    if (
      absorbableEndpointItems.some((item) => item.value === selectedEndpointId)
    ) {
      return;
    }

    setSelectedEndpointId('');
  }, [absorbableEndpointItems, selectedEndpointId]);

  const transferSelectedRows = async () => {
    if (!canConfirm) {
      return;
    }

    try {
      await mutateAsync({
        applicationId,
        sourceEndpointId,
        harEntryIds: selectedRows.map((row) => row.id),
        target:
          mode === 'existing'
            ? { kind: 'existing', endpointId: selectedEndpointId }
            : toCreateFromHarsTarget({
                groups: createGroups,
                pathsByGroupId,
              }),
      });
      onMoved?.();
      onOpenChange(false);
    } catch (error) {
      if (
        error instanceof Error &&
        isReassociateHarEntriesConflictError(error) &&
        error.collision === 'active'
      ) {
        const existingTargetId = existingTargetIdFromActiveCollision({
          suggestedEndpointId: error.suggestedEndpointId,
          sourceEndpointId,
        });

        if (existingTargetId) {
          setMode('existing');
          setSelectedEndpointId(existingTargetId);
        }

        return;
      }
    }
  };

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }

    void transferSelectedRows();
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => onOpenChange(open)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxW="md"
          w="full"
          {...automationId(REQUESTS_REASSOCIATE_AUTOMATION_IDS.dialog)}
        >
          <VStack gap="4" p="4" alignItems="stretch">
            <VStack gap="1" alignItems="flex-start">
              <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
              <LegacyDialog.Description color="fg.muted">
                {t('description')}
              </LegacyDialog.Description>
            </VStack>
            <SegmentGroupHorizontal
              value={mode}
              onValueChange={({ value }) => {
                if (value === 'existing' || value === 'createFromHars') {
                  setMode(value);
                  setEndpointQuery('');
                }
              }}
              options={[
                {
                  value: 'existing',
                  label: t('existing'),
                  dataTestid: REQUESTS_REASSOCIATE_AUTOMATION_IDS.modeExisting,
                },
                {
                  value: 'createFromHars',
                  label: t('createFromHars'),
                  dataTestid: REQUESTS_REASSOCIATE_AUTOMATION_IDS.modeCreate,
                },
              ]}
            />
            {mode === 'existing' ? (
              <Combobox.Root
                collection={collection}
                value={selectedEndpointId ? [selectedEndpointId] : []}
                inputValue={endpointQuery}
                onValueChange={(details) =>
                  setSelectedEndpointId(details.value[0] ?? '')
                }
                onInputValueChange={(details) =>
                  setEndpointQuery(details.inputValue)
                }
                disabled={showNoValidEndpoints}
                openOnClick={hasAbsorbableEndpoints}
                size="sm"
                width="full"
                positioning={{ strategy: 'fixed' }}
              >
                <Combobox.Label>
                  <LabelDefault>{t('endpointPlaceholder')}</LabelDefault>
                </Combobox.Label>
                {showNoValidEndpoints ? (
                  <BodyDefault color="fg.error">
                    {t('noValidEndpoints')}
                  </BodyDefault>
                ) : null}
                <Combobox.Control>
                  <Combobox.Input
                    placeholder={t('endpointPlaceholder')}
                    asChild
                  >
                    <Input
                      size="sm"
                      disabled={showNoValidEndpoints}
                      {...automationId(
                        REQUESTS_REASSOCIATE_AUTOMATION_IDS.endpointCombobox,
                      )}
                    />
                  </Combobox.Input>
                  <Combobox.Trigger asChild>
                    <IconButton
                      variant="link"
                      size="xs"
                      disabled={showNoValidEndpoints}
                      aria-label={t('endpointPlaceholder')}
                      {...automationId(
                        REQUESTS_REASSOCIATE_AUTOMATION_IDS.endpointCombobox,
                        'trigger',
                      )}
                    >
                      <ArrowUpDownIcon size={14} />
                    </IconButton>
                  </Combobox.Trigger>
                </Combobox.Control>
                <Combobox.Positioner>
                  <Combobox.Content maxH="300px" overflowY="auto">
                    <Combobox.List>
                      <Combobox.ItemGroup>
                        {collection.items.map((item) => (
                          <Combobox.Item
                            key={item.value}
                            item={item}
                            {...automationId(
                              REQUESTS_REASSOCIATE_AUTOMATION_IDS.endpointCombobox,
                              item.value,
                            )}
                          >
                            <Combobox.ItemText>{item.label}</Combobox.ItemText>
                            <Combobox.ItemIndicator>
                              <Tick01Icon size={14} />
                            </Combobox.ItemIndicator>
                          </Combobox.Item>
                        ))}
                      </Combobox.ItemGroup>
                    </Combobox.List>
                  </Combobox.Content>
                </Combobox.Positioner>
              </Combobox.Root>
            ) : (
              <ReassociateCreateFromHarsFields
                groups={createGroups}
                pathsByGroupId={pathsByGroupId}
                createCollision={createCollision}
                onPathChange={(groupId, path) =>
                  setPathsByGroupId((current) => ({
                    ...current,
                    [groupId]: path,
                  }))
                }
              />
            )}
            <HStack gap="2" justifyContent="flex-end">
              <LegacyDialog.CloseTrigger asChild>
                <Button
                  variant="outline"
                  size="xs"
                  disabled={isPending}
                  {...automationId(REQUESTS_REASSOCIATE_AUTOMATION_IDS.cancel)}
                >
                  {tCommon('cancel')}
                </Button>
              </LegacyDialog.CloseTrigger>
              <Button
                size="xs"
                onClick={handleConfirm}
                loading={isPending || undefined}
                disabled={isPending || !canConfirm}
                {...automationId(REQUESTS_REASSOCIATE_AUTOMATION_IDS.confirm)}
              >
                {tCommon('confirm')}
              </Button>
            </HStack>
          </VStack>
          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton
              aria-label={tCommon('close')}
              variant="ghost"
              size="xs"
              disabled={isPending}
            >
              <Cancel01Icon size={14} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
